import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  LabelList,
} from "recharts";
import { DisaggregatedMetric, DistributionData, GeoDetails, GeoTag, MetricMetadata, RawIndicatorData } from "../../services/types";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { formatValue, formatValueBase } from "../../utils/helpers";
import { useEffect, useMemo, useRef, useState } from "react";
import { fetchDistributionData, fetchIndicatorBinCount, fetchMetricMetadata } from "../../utils/dashboardUtils";
import MetricMetadataModal from "../MetricMetadataModal";
import metricMetadataIcon from '../../assets/metric-metadata-icon-blue.svg';

interface RaceEthnicityBarChartCardProps {
    raceMetric: DisaggregatedMetric | undefined;
    disaggData: DisaggregatedMetric[] | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    ethnicityMetric?: DisaggregatedMetric;
    barColor?: string;
    height: string;
    background: string;
    showMetadataIcon?: boolean;
}

const CustomBarLabel = (props: any) => {
	const { x, y, width, height, value, payload } = props;

	if (payload?.isSpacer || value == null) return null;

	return (
		<text
		x={x + width + 6}
		y={y + height / 2}
		dy={3}
		fill="#334155"
		fontSize={9}
		textAnchor="start"
		>
		{formatValueBase(value, 'integer')}
		</text>
	);
};

const CustomTooltipBar = ({ active, payload, label }: any) => {
	if (!active || !payload || !payload.length) return null;

	const barData = payload[0]?.payload;

	if (barData?.isSpacer) return null;

	return (
		<div className="bg-white p-2 border border-slate-200 rounded shadow-sm text-xs">
		<p>{`${label.replace(/\bNH\b/, "Non-Hispanic")}`}</p>
		<p>{`Count: ${formatValueBase(barData.barLabel, "integer")}`}</p>
		<p>{`Share: ${formatValue(payload[0].value, barData.unit)}`}</p>
		</div>
	);
};

const CustomTick = ({ x, y, payload }: any) => {
	const maxLineLength = 25;
	const label = typeof payload.value === "string" ? payload.value : String(payload.value);

	if (label.trim() === "") return null; // don't render tick for spacer

	const words = label.split(" ");
	const lines: string[] = [];
	let currentLine = "";

	words.forEach((word: any) => {
		if ((currentLine + word).length <= maxLineLength) {
		currentLine += (currentLine ? " " : "") + word;
		} else {
		if (currentLine) lines.push(currentLine);
		currentLine = word;
		}
	});
	if (currentLine) lines.push(currentLine);

	if (lines.length === 1) {
		return (
		<text
			x={x}
			y={y}
			dy={2}
			textAnchor="end"
			fill="#666"
			style={{ whiteSpace: "nowrap", fontSize: 9 }}
		>
			{lines[0]}
		</text>
		);
	}

	return (
		<text x={x} y={y} textAnchor="end" fill="#666" style={{ fontSize: 9 }}>
		{lines.map((line, i) => (
			<tspan key={i} x={x} dy={i === 0 ? -1 : 9}>
			{line}
			</tspan>
		))}
		</text>
	);
};

const RaceEthnicityBarChartCard = ({
	raceMetric,
	disaggData,
	geoDetails,
	geoTag,
	selectionActive,
	ethnicityMetric,
	height,
	background,
	barColor = "#337AB7",
	showMetadataIcon = true
}: RaceEthnicityBarChartCardProps) => {
	if (
		!raceMetric ||
		!raceMetric.versions ||
		raceMetric.versions.length === 0 ||
		!raceMetric.versions[0].disaggregations
	) {
		return (
		<div className="h-24 flex items-center justify-center text-xs text-slate-500">
			<Skeleton height={24} width={"100%"} />
		</div>
		);
	}

	const id = raceMetric.id;

	const [showMetadata, setShowMetadata] = useState(false);
	const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
	const [distributionData, setDistributionData] = useState<RawIndicatorData[] | null>(null);
	const [countyDistributionData, setCountyDistributionData] = useState<DistributionData[] | null>(null);

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);

	// Refs for metadata modal
	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const modalId = `metric-metadata-modal-${id ?? 'main'}`;
	const clickDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const fetchCache = useRef<Record<string, Promise<any> | undefined>>({});

	function fetchWithCache<T>(key: string, fetchFn: () => Promise<T>): Promise<T> {
		if (fetchCache.current[key]) {
			// Return ongoing request if already in-flight
			return fetchCache.current[key] as Promise<T>;
		}

		const promise = fetchFn().finally(() => {
			delete fetchCache.current[key];
		});

		fetchCache.current[key] = promise;
		return promise;
	};

	// Prefetch on mount (throttled sequentially)
	useEffect(() => {
		// Only fetch if not already cached or loaded
		if (!metadata) {
			fetchWithCache(`metadata-${id}`, () => fetchMetricMetadata(id))
			.then(res => res?.data && setMetadata(res.data))
			.catch(console.error);
		}

		if (!distributionData) {
			const distKey = `distData-${id}-${geoDetails.map(d => d.lod).join(',')}-${geoTag?.type}-${geoTag?.value}`;
			fetchWithCache(distKey, () => fetchIndicatorBinCount(id, geoDetails, geoTag))
			.then(res => res && setDistributionData(res))
			.catch(console.error);
		}

		if (!countyDistributionData) {
			const countyKey = `countyDist-${id}-53033`;
			fetchWithCache(countyKey, () => fetchDistributionData(id, [], null))
			.then(res => res && setCountyDistributionData(res))
			.catch(console.error);
		}
	}, [id, geoDetails, geoTag]);

	const handleClick = async () => {
		if (clickDebounceRef.current) clearTimeout(clickDebounceRef.current);

		clickDebounceRef.current = setTimeout(async () => {
			setShowMetadata((prev) => !prev);
			setLoading(true);

			try {
			const [metadataResponse, distributionDataResponse, countyDistributionDataResponse] = await Promise.all([
				metadata
				? Promise.resolve({ data: metadata })
				: fetchWithCache(`metadata-${id}`, () => fetchMetricMetadata(id)),

				distributionData
				? Promise.resolve(distributionData)
				: fetchWithCache(`distData-${id}-${geoDetails.map(d => d.lod).join(',')}-${geoTag?.type}-${geoTag?.value}`, () =>
					fetchIndicatorBinCount(id, geoDetails, geoTag)
					),

				countyDistributionData
				? Promise.resolve(countyDistributionData)
				: fetchWithCache(`countyDist-${id}-53033`, () =>
					fetchDistributionData(id, [], null)
					),
			]);

			if (!metadata) setMetadata(metadataResponse?.data || null);
			if (!distributionData) setDistributionData(distributionDataResponse || null);
			if (!countyDistributionData) setCountyDistributionData(countyDistributionDataResponse || null);

			} catch (error) {
			const errorMessage = (error as Error).message || 'An unexpected error occurred.';
			console.error('Error fetching data:', error);
			setError(new Error(`Failed to load data: ${errorMessage}`));
			} finally {
			setLoading(false);
			}
		}, 150);
	};

	const unit = raceMetric.versions[0]?.unit;
	const name = raceMetric.name;

	const getSortedRaceData = (metric: DisaggregatedMetric | undefined) => {
		if (!metric?.versions?.[0].disaggregations) return [];
		return [...metric.versions[0].disaggregations]
		.sort((a, b) => b.value - a.value)
		.map((d) => ({
			name: d.name,
			value: d.value ?? 0,
			unit: unit,
			barLabel: d.percent_calc?.n ?? null,
		}));
	};

	const getEthnicityData = (metric: DisaggregatedMetric | undefined) => {
		const disaggregations = metric?.versions?.[0]?.disaggregations ?? [];

		const hispanic = disaggregations.find(d => d.name === "Hispanic or Latino");
  		if (!hispanic?.percent_calc) return [];

		return [
			{
			name: "Hispanic or Latino",
			value: hispanic.percent_calc.n / hispanic.percent_calc.d,
			unit,
			barLabel: hispanic.percent_calc.n,
			},
		];
	};

	const raceData = useMemo(() => getSortedRaceData(raceMetric), [raceMetric, unit]);
	const ethnicityData = useMemo(() => getEthnicityData(ethnicityMetric), [ethnicityMetric, unit]);

	const chartData = useMemo(
	() =>
		ethnicityData.length > 0
		? [
			...raceData,
			{ name: "", value: 0, unit, barLabel: null, isSpacer: true },
			...ethnicityData,
			]
		: raceData,
	[raceData, ethnicityData, unit]
	);

	if (chartData.length === 0) {
		return (
		<div className="h-24 flex items-center justify-center text-xs text-slate-500">
			No data available
		</div>
		);
	}

	return (
		<>
		<div className="relative overflow-hidden group hover:shadow-md transition-all border rounded-xl bg-white">
			<div className="p-4 pb-1">
				<div className="flex items-start justify-between mb-3">
					<h3 className="text-sm font-semibold text-port-text">
						{name}
					</h3>
					
					{showMetadataIcon && 
						<button
							type="button"
							onClick={handleClick}
							aria-haspopup="dialog"
							aria-expanded={showMetadata ? 'true' : 'false'}
							aria-controls={modalId}
							aria-label={`Additional information about ${name}`}
							className="
							inline-flex h-6 w-6 items-center justify-center rounded
							transition-transform hover:scale-105 focus:scale-105
							focus-visible:outline-none
							focus-visible:ring-2 focus-visible:ring-offset-2
							focus-visible:ring-neutral-900 focus-visible:ring-offset-white
							dark:focus-visible:ring-white dark:focus-visible:ring-offset-neutral-900
							"
						>
							<img src={metricMetadataIcon} alt="" aria-hidden="true" className="h-3 w-3" draggable="false" />
						</button>
					}
				</div>
			</div>

			<div className={`${height} ${background}`}>
			<ResponsiveContainer width="100%" height="100%">
				<BarChart
				data={chartData}
				layout="vertical"
				margin={{ top: 2, right: 40, left: 40, bottom: 0 }}
				barCategoryGap="8%"
				barGap={-2}
				>
				<XAxis
					type="number"
					tick={{ fontSize: 9 }}
					tickFormatter={(value) => formatValue(value, unit, id)}
				/>
				<YAxis
					type="category"
					dataKey="name"
					tick={<CustomTick />}
					width={100}
					interval={0}
					tickLine={false}
				/>
				<Tooltip
					content={<CustomTooltipBar />}
					cursor={{ fill: "transparent" }}
				/>
				<Bar dataKey="value" fill={barColor} activeBar={false} isAnimationActive={false}>
					<LabelList dataKey="barLabel" content={<CustomBarLabel />} />
				</Bar>
				</BarChart>
			</ResponsiveContainer>
			</div>

			<div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2258e5]/50 to-[#00ac4e]/50 opacity-0 group-hover:opacity-100 transition-opacity" />
		</div>

		<MetricMetadataModal
			isOpen={showMetadata}
			onClose={handleClick}
			id={id}
			metadata={metadata}
			disaggData={disaggData}
			stateData={null}
			nationalData={null}
			distributionData={distributionData}
			countyDistributionData={countyDistributionData}
			selectionActive={selectionActive}
			loading={loading}
			error={error}
			triggerRef={triggerRef}
			modalId={modalId}
		/>
		</>
	);
};

export default RaceEthnicityBarChartCard;
